import { Component, forwardRef, Input, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
	AbstractControl,
	ControlValueAccessor,
	FormControl,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validator
} from '@angular/forms';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';

@Component({
	selector: 'app-phone-input',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, NgxMatInputTelComponent],
	templateUrl: './phone-input.component.html',
	styleUrls: ['./phone-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => PhoneInputComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => PhoneInputComponent),
			multi: true
		}
	]
})
export class PhoneInputComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy, AfterViewInit {
	@ViewChild('telInput') telInput?: NgxMatInputTelComponent;

	@Input() preferredCountries: string[] = ['ph'];
	@Input() enablePlaceholder: boolean = true;
	@Input() enableSearch: boolean = true;
	@Input() name: string = 'phone';
	@Input() id: string = 'phone';
	@Input() placeholder: string = '';

	phoneControl = new FormControl('');
	disabled: boolean = false;

	private onChange = (value: string) => {};
	private onTouched = () => {};
	private onValidatorChange = () => {};
	private subscription = new Subscription();
	private lastEmitted: string | null = null;
	private onInputEvent = () => this.emitValue();
	private onBlurEvent = () => this.onTouched();

	constructor(private elementRef: ElementRef) {}

	ngOnInit() {
		this.subscription.add(this.phoneControl.valueChanges.subscribe(() => this.emitValue()));
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();

		const input = this.getInputElement();
		if (input) {
			input.removeEventListener('input', this.onInputEvent);
			input.removeEventListener('blur', this.onBlurEvent);
		}
	}

	focusInput() {
		const input = this.getInputElement();
		if (input) {
			input.focus();
		}
	}

	private getInputElement(): HTMLInputElement | null {
		return this.elementRef.nativeElement.querySelector('input');
	}

	/**
	 * The value the form should hold: the normalised E.164 number once it is valid,
	 * otherwise whatever is actually typed.
	 *
	 * ngx-mat-input-tel cannot be trusted for this. It does `value = numberInstance?.number`,
	 * which is undefined for anything it can't fully parse — its own raw-text fallback
	 * sits in a catch block that never runs, because parsePhoneNumberFromString returns
	 * undefined rather than throwing. So a half-typed number reached the form as empty
	 * and tripped `required`. It also skips propagation entirely when the input is
	 * cleared, which would otherwise leave the last value stranded in the form.
	 */
	private currentValue(): string {
		const input = this.getInputElement();
		const raw = input ? input.value.trim() : '';
		if (!raw) {
			return '';
		}

		const parsed = this.telInput?.numberInstance;
		return parsed && parsed.isValid() ? parsed.number : raw;
	}

	private emitValue(): void {
		const value = this.currentValue();
		if (value === this.lastEmitted) {
			return;
		}

		this.lastEmitted = value;
		this.onChange(value);
	}

	ngAfterViewInit() {
		if (this.telInput) {
			this.telInput.preferredCountries = this.preferredCountries;
			this.telInput.enablePlaceholder = false;
			this.telInput.enableSearch = this.enableSearch;
			this.telInput.name = this.name;
			this.telInput.id = this.id;
			// The package renders this on its own <input>, to the right of the country
			// selector. An overlay would sit on top of the flag and dial code instead.
			this.telInput.inputPlaceholder = this.placeholder;

			// Switching country re-tests the same digits against different rules, so both
			// the value and the error have to be recomputed.
			this.subscription.add(
				this.telInput.countryChanged.subscribe(() => {
					this.emitValue();
					this.onValidatorChange();
				})
			);

			this.applyPreferredCountry();
		}

		// Driven off the DOM rather than the package's own propagation, which stays
		// silent for input it can't parse and for a cleared field. See currentValue().
		const input = this.getInputElement();
		if (input) {
			input.addEventListener('input', this.onInputEvent);
			input.addEventListener('blur', this.onBlurEvent);
		}

		setTimeout(() => {
			this.applyPreferredCountry();
			// The first validate() ran before telInput existed and returned null; now
			// that the package has parsed the value, ask for a real verdict.
			this.onValidatorChange();
		}, 100);
	}

	/**
	 * Empty is left to Validators.required — reporting both at once would show
	 * "this is required" for a field that does contain a number.
	 */
	validate(control: AbstractControl): ValidationErrors | null {
		const value = control.value == null ? '' : String(control.value).trim();
		if (!value) {
			return null;
		}

		// Runs before ngAfterViewInit on the initial patch of an existing supplier.
		// There is nothing parsed to judge against yet, so stay quiet and re-validate
		// once the input is wired up.
		if (!this.telInput) {
			return null;
		}

		const numberInstance = this.telInput.numberInstance;
		if (numberInstance && numberInstance.isValid()) {
			return null;
		}

		return {
			phoneCountryMismatch: {
				country: this.telInput.selectedCountry?.name || '',
				dialCode: this.telInput.selectedCountry?.dialCode || ''
			}
		};
	}

	registerOnValidatorChange(fn: () => void): void {
		this.onValidatorChange = fn;
	}

	private applyPreferredCountry(): void {
		const iso = this.preferredCountries?.[0];
		if (!this.telInput || !iso) {
			return;
		}

		try {
			const country = this.telInput.getCountry(iso.toUpperCase() as any);
			if (country) {
				this.telInput.selectedCountry = country;
			}
		} catch {
			// Keep package default if ISO is invalid.
		}
	}

	writeValue(value: string): void {
		// Seeds lastEmitted so loading an existing supplier doesn't echo the same
		// value straight back to the form as a user edit.
		this.lastEmitted = value || '';
		this.phoneControl.setValue(value || '', { emitEvent: false });
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.phoneControl.disable({ emitEvent: false });
		} else {
			this.phoneControl.enable({ emitEvent: false });
		}
	}
}
